/**
 * FERP Portal - Dashboard Schema List Page
 *
 * Dashboard schemas define layout, widgets, and data sources for role-based
 * dashboards. Currently a single schema is seeded; the backend dashboard
 * schema API is not yet implemented.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: displays known config metadata
 * - Admin view layer only
 */

import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconDashboard, IconInfoCircle, IconLayoutDashboard } from '@tabler/icons-react';

const KNOWN_SCHEMAS = [
  {
    id: 'default-staff-dashboard',
    name: 'Staff Dashboard',
    role: 'staff',
    description: 'Frontline staff view: my tasks today, upcoming deadlines, recent activity. Optimized for mobile.',
  },
  {
    id: 'default-supervisor-dashboard',
    name: 'Supervisor Dashboard',
    role: 'supervisor',
    description: 'Operations view: team workload, task distribution, compliance status, escalation queue.',
  },
];

export function DashboardSchemaListPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Dashboard Schemas</Title>
            <Text c="dimmed" size="sm">
              Role-based dashboard layout and widget configurations
            </Text>
          </div>
          <Badge variant="light" color="grape" size="lg">
            {KNOWN_SCHEMAS.length} Schemas
          </Badge>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="Not Yet Query-Backed">
          Dashboard schemas are an early-stage config type. A dedicated GraphQL API
          is planned but not yet implemented. The schemas below reflect the current
          seeded configuration used by StaffDashboard and SupervisorDashboard pages.
        </Alert>

        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="grape">
              <IconLayoutDashboard size={18} />
            </ThemeIcon>
            <Text fw={600}>How Dashboard Schemas Work</Text>
          </Group>
          <Text size="sm" c="dimmed">
            YAML config defines widget layout (grid positions, data sources, refresh intervals).
            Role-based dashboards (StaffDashboard, SupervisorDashboard) consume the schema at
            build time. Future: dynamic dashboard rendering from backend schema.
          </Text>
        </Card>

        <Stack gap="sm">
          {KNOWN_SCHEMAS.map((schema) => (
            <Card key={schema.id} withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <ThemeIcon variant="light" color="grape" size="sm">
                    <IconDashboard size={14} />
                  </ThemeIcon>
                  <div>
                    <Text fw={500}>{schema.name}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">{schema.id}</Text>
                  </div>
                </Group>
                <Badge variant="light" color="grape" size="sm">
                  {schema.role}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">{schema.description}</Text>
            </Card>
          ))}
        </Stack>

        <Text size="xs" c="dimmed">
          To add or modify dashboard schemas, edit{' '}
          <Anchor size="xs" href="#" onClick={(e) => e.preventDefault()}>
            common/configs/seed/dashboard-schemas/*.yaml
          </Anchor>{' '}
          and re-seed.
        </Text>
      </Stack>
    </Container>
  );
}

export default DashboardSchemaListPage;
